import React, { memo } from 'react';
import PropTypes from 'prop-types';
import makeStyles from '@material-ui/core/styles/makeStyles';
import remove from 'lodash/remove';
import debounce from 'lodash/debounce';
import TodayIcon from '@material-ui/icons/Today';
import { Formik, Form, getIn } from 'formik';
import { Grid } from '@material-ui/core';
import { useDispatch, connect } from 'react-redux';
import get from 'lodash/get';
import find from 'lodash/find';
import { isEmpty, set, keyBy, compact, uniq } from 'lodash';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { Router, useTranslation } from '../../../../src/i18n';
import EditProductionCalendar from '../EditProductionCalendar';
import Button from '../../../../components/Button';
import { TP, PERFORMANCE_TYPES_ID, PRODUCTION_ADDED_IDENTIFIERS_IDS, VISIBILITY } from '../../../../constants/consts';
import Popover from '../../../../components/Popover';
import PerformanceEditCard from '../PerformanceEditCard';
import { performanceStatuses } from '../../../../components/PerformanceCardHeader/PerformanceDate';
import Title from '../../../../components/Title';
import TimeAddModal from '../TimeAddModal';
import {
  dateSendFormat,
  getOption,
  createSelectFieldOptions,
  scrollToInvalidField,
  getCurrentTimezoneDate,
  canDoProductionAction,
  checkDateForProductionDeletion,
} from '../../../../utils/common';
import { cardFields, publishedCardFields } from '../../constants';
import { createEntity, getList, getWholeList } from '../../../App/actions';
import {
  PRODUCTION_DATES,
  VENUES,
  PRODUCTION_CASTS,
  COUNTRY_CITY_ENDPOINT,
  VENUES_STAGES_ENDPOINT,
} from '../../../../constants/endpointConstants';
import {
  updateProductionPerformancesAction,
  getVenuesAction,
  cleanOptionsValuesAction,
  getProductionCastAction,
  getProductionDatesAction,
  getPerformanceCitiesAction,
  clearPerformanceCitiesAction,
  clearPerformanceStagesAction,
  getPerformanceStagesAction,
  getCompanyVenuesAction,
} from '../../actions';
import {
  selectPerformancesFormValues,
  selectVenuesOptions,
  selectLoadingFieldName,
  selectProductionUsedRolesAndProfessions,
  selectCasts,
  selectPerformanceCitiesOptions,
  selectPerformanceStagesOptions,
  selectDatesLoading,
  selectProductionCompanyVenues,
  selectArtistDates,
} from '../../selectors';
import { selectUserConstants } from '../../../../store/selectors';
import StandardizedTime from '../StandardizedTime';
import StandardizedVenue, { getDefaultVenue } from '../StandardizedVenue';
import CastEditModal from '../CastEditModal';
import { getPerformancesValuesForUpdate, getQueryFuncForEditStep } from '../../utils';
import BulkBox from '../BulkBox';
import TitleWithInfo from '../../../../components/TitleWithInfo';
import usePermissions from '../../../../utils/permissions';
import { selectActiveProfileId } from '../../../App/selectors';
import StepperButtonsBlock from '../../../../components/Stepper/StepperButtonsBlock';
import SelectDatesCard from '../../ArtistsProductionUpdate/SelectDatesCard';
import { isProductionType } from '../../../../utils/editCommon';
import ConfirmDeleteModal from '../../../../components/EditComponents/ConfirmDeleteModal';

const useStyles = makeStyles(theme => ({
  calendarPopup: {
    maxWidth: 'calc(100% - 240px)',
    [theme.breakpoints.down('sm')]: {
      left: '0 !important',
      maxWidth: '100%',
    },
  },
  datesButton: {
    marginBottom: theme.spacing(2.5),
  },
  performanceDetailsTitle: {
    fontWeight: 'bold',
    display: 'block',
    marginTop: theme.spacing(2.5),
  },
}));

const findDefaultVenue = (venues, companyId) => {
  let defaultVenue = null;
  venues.forEach(v => {
    const currentVenueCompany = find(v?.companies, { id: companyId });
    if (currentVenueCompany?.isDefault) {
      defaultVenue = v;
    }
  });
  return defaultVenue;
};

const LiveEvents = ({
  editingProfile,
  production,
  casts,
  existPerformancesValues,
  loadingValue,
  venuesOptions,
  citiesOptions,
  productionUsedRoles,
  stagesOptions,
  userConstants,
  datesLoading,
  productionCompanyVenues,
  activeProfileId,
  type,
  artistDates,
  sectionStep,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const FormRef = React.createRef(null);
  const { productionPermissions } = usePermissions(activeProfileId);
  const isCompanyType = type === 'company';
  const isArtistType = type === 'artist';
  //  TODO Add actions for festival venues

  const stepActionFunc = React.useCallback(() => getQueryFuncForEditStep(type), [type]);

  const doNextStep = () => {
    const queryFunc = stepActionFunc();
    const { pathname, query } = queryFunc({ entity: editingProfile, production, step: sectionStep + 1 });
    Router.push({ pathname, query });
  };

  const nextStepAction = () => {
    get(FormRef.current, 'submitForm', () => {})();
    scrollToInvalidField();
  };

  const backStepAction = () => {
    const queryFunc = stepActionFunc();
    const { pathname, query } = queryFunc({ entity: editingProfile, production, step: sectionStep - 1 });
    Router.push({ pathname, query });
  };

  // eslint-disable-next-line camelcase
  const isPublishedProduction = Boolean(production?.published_at);

  // ARTIST STATE VALUES
  const [prodArtistCasts, setProductionArtistsCasts] = React.useState([]);
  const [playedDates, setPlayedDates] = React.useState([]);
  // ///////////////////

  const canDeletePublishedPerformance = React.useCallback(
    () => {
      const addedIdetifier = () => {
        switch (type) {
          case 'company':
            return PRODUCTION_ADDED_IDENTIFIERS_IDS.COMPANY;
          case 'festival':
            return PRODUCTION_ADDED_IDENTIFIERS_IDS.FESTIVAL;
          case 'artist':
            return PRODUCTION_ADDED_IDENTIFIERS_IDS.ARTIST;
          default:
            return '';
        }
      };
      const hasPermissionsToDeleteProduction = canDoProductionAction(
        productionPermissions?.canDelete,
        addedIdetifier(),
        editingProfile?.id,
        production,
      );
      if (production?.published_at) {
        return (
          productionPermissions?.canDeleteAsAdmin ||
          (hasPermissionsToDeleteProduction &&
            checkDateForProductionDeletion(
              get(userConstants, '[0].MINIMUM_PRODUCTION_DELETION_DATE'),
              production?.published_at,
            ))
        );
      }
      return productionPermissions?.canDeleteAsAdmin || hasPermissionsToDeleteProduction;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [production, userConstants],
  );

  const mergeTimeWithCast = (time, cast, crew) => {
    const defaultCasts = defaultProductionCastFormValues();
    return {
      time,
      cast: cast || defaultCasts?.cast || {},
      crew: crew || defaultCasts?.crew || {},
    };
  };

  const [performancesChosenDates, setPerformancesChosenDates] = React.useState(
    !isPublishedProduction ? Object.keys(existPerformancesValues || []).map(d => getCurrentTimezoneDate(d)) : [],
  );
  // eslint-disable-next-line no-unused-vars
  const [performancesDisabledDates, setPerformancesDisabledDates] = React.useState(
    isPublishedProduction ? Object.keys(existPerformancesValues || []).map(d => getCurrentTimezoneDate(d)) : [],
  );

  const [defaultTimeValues, setDefaultTimeValues] = React.useState([]);
  const [defaultTimesModalState, setDefaultTimesModalState] = React.useState(false);
  const [updateTimesFunc, setUpdateTimesFunc] = React.useState(() => () => {});
  const [defaultVenueModalState, setDefaultVenueModalState] = React.useState(false);
  const [updateVenuesFunc, setUpdateVenuesFunc] = React.useState(() => () => {});
  const [defaultVenueValues, setDefaultVenueValues] = React.useState(getDefaultVenue(null));

  const allLiveDatesOfProduction = React.useCallback(() => [...performancesChosenDates, ...performancesDisabledDates], [
    performancesChosenDates,
    performancesDisabledDates,
  ]);

  //  to get list if redirect from front
  React.useEffect(() => {
    if (isEmpty(casts)) {
      dispatch(
        getWholeList({
          endpoint: PRODUCTION_CASTS(production?.id),
          sagaRoutine: getProductionCastAction,
          params: { queryParams: { as_edit: true } },
        }),
      );
    }
    if (isEmpty(existPerformancesValues)) {
      dispatch(
        getWholeList({
          endpoint: PRODUCTION_DATES(production?.id),
          sagaRoutine: getProductionDatesAction,
          params: { queryParams: { as_edit: true } },
        }),
      );
    }
    if (isEmpty(productionCompanyVenues?.data) && !productionCompanyVenues?.loadedOnce && isCompanyType) {
      dispatch(
        getWholeList({
          endpoint: VENUES,
          sagaRoutine: getCompanyVenuesAction,
          params: { queryParams: { company_id: editingProfile?.id, as_edit: true } },
        }),
      );
    }
    if (isArtistType) {
      const artistCast = casts.filter(castItem => castItem?.profile?.id === editingProfile?.id);
      const updatedRoles = [];
      let selectedDates = [];
      artistCast.forEach(role => {
        const isRole = Boolean(role?.workRole?.id || role?.workRoleReference);
        selectedDates = [...selectedDates, ...get(artistDates, `artistDates.${role?.id}`, [])];
        updatedRoles.push({
          ...role,
          type: isRole ? 'role' : 'profession',
        });
      });
      setProductionArtistsCasts(updatedRoles);
      setPlayedDates(uniq(selectedDates));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [production, editingProfile]);

  React.useEffect(() => {
    if (isCompanyType) {
      const defaultVenue = findDefaultVenue(productionCompanyVenues?.data, editingProfile?.id);
      setDefaultVenueValues(
        defaultVenue
          ? getDefaultVenue(defaultVenue)
          : {
              [cardFields.city.name]: editingProfile?.city?.id ? getOption(editingProfile?.city) : '',
              [cardFields.country.name]: editingProfile?.country?.id ? getOption(editingProfile?.country) : '',
            },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionCompanyVenues, editingProfile]);

  const updatePerformancesDates = dates => {
    const values = { ...FormRef.current?.values };
    const updateFunc = FormRef.current?.resetForm;
    const performanceChosenDatesFormatted = dates.map(date => dateSendFormat(date));
    const { cast, crew } = defaultProductionCastFormValues();
    performanceChosenDatesFormatted.forEach(dateKey => {
      if (!values[dateKey]) {
        values[dateKey] = {
          date: dateKey,
          isPublished: false,
          status: performanceStatuses.regular,
          cast,
          crew,
          [cardFields.time.name]:
            defaultTimeValues.length > 0
              ? defaultTimeValues.map(time => mergeTimeWithCast(time, cast, crew))
              : [mergeTimeWithCast(null, cast, crew)],
          [cardFields.isTour.name]: false,
          [cardFields.isFestivalPart.name]: isProductionType(
            production?.performanceTypes,
            PERFORMANCE_TYPES_ID.FESTIVAL,
          ),
          [publishedCardFields.cancel.name]: false,
          [cardFields.festival.name]: production?.festivals?.length > 0 ? getOption(production?.festivals[0]) : null,
          ...defaultVenueValues,
        };
      }
    });
    const deletedDates = [];
    // to allow remove perf from calendar
    Object.keys(values).forEach(key => {
      if (
        ![...performanceChosenDatesFormatted, ...performancesDisabledDates.map(date => dateSendFormat(date))].includes(
          key,
        )
      ) {
        deletedDates.push(key);
        delete values[key];
      }
    });
    updateFunc({ values });
    if (isArtistType) {
      const updatedPlayedDates = [...playedDates];
      // to add selected dates if there are new dates
      // eslint-disable-next-line no-unused-expressions
      performanceChosenDatesFormatted?.forEach(date => {
        if (!performancesChosenDates?.map(d => dateSendFormat(d))?.includes(date)) {
          updatedPlayedDates.push(date);
        }
      });
      updatedPlayedDates.filter(date => !deletedDates.includes(date));
      setPlayedDates(updatedPlayedDates);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  // //////////////////////////
  // START ARTIST TYPE FUNCTIONS
  const updateArtistDatesCast = (dates, isSelected) => {
    const values = { ...FormRef.current?.values };
    const updateFunc = FormRef.current?.resetForm;
    const isRole = role => role?.type === 'role';
    const castFields = role => ({
      valuesArray: isRole(role) ? 'cast' : 'crew',
      id: isRole(role) ? 'workRole.id' : 'profession.id',
    });
    let newSelectedDates = [...playedDates];
    // eslint-disable-next-line no-unused-expressions
    dates?.forEach(date => {
      let timesValues = [...getIn(values, `${date}.${cardFields.time.name}`)];
      if (isSelected) {
        newSelectedDates.push(date);
        // eslint-disable-next-line no-unused-expressions
        prodArtistCasts?.forEach(castRecord => {
          const { valuesArray, id } = castFields(castRecord);
          timesValues = timesValues?.map(timeV => {
            const rolesCastValues = getIn(timeV, `${valuesArray}.${get(castRecord, id)}`, []) || [];
            return {
              ...timeV,
              [valuesArray]: {
                ...get(timeV, valuesArray),
                [get(castRecord, id)]: [
                  ...rolesCastValues,
                  getOption(castRecord, 'profile.id', 'profile.name', {}, true),
                ],
              },
            };
          });
        });
      } else {
        newSelectedDates = newSelectedDates?.filter(d => d !== date);
        // eslint-disable-next-line no-unused-expressions
        prodArtistCasts?.forEach(castRecord => {
          const { valuesArray, id } = castFields(castRecord);
          timesValues = timesValues?.map(timeV => {
            const rolesCastValues = getIn(timeV, `${valuesArray}.${get(castRecord, id)}`, []) || [];
            const newArtistsArray = rolesCastValues?.filter(record => record?.id !== castRecord?.id);
            return {
              ...timeV,
              [valuesArray]: {
                ...get(timeV, valuesArray),
                [get(castRecord, id)]: newArtistsArray?.length > 0 ? newArtistsArray : [undefined],
              },
            };
          });
        });
      }
      set(values, `${date}.${cardFields.time.name}`, timesValues);
    });
    updateFunc({ values });
    setPlayedDates(uniq(newSelectedDates));
  };
  // END ARTIST TYPE FUNCTIONS
  // //////////////////////////

  const getFestivalsOptions = React.useCallback(() => createSelectFieldOptions(production?.festivals, 'id', 'name'), [
    production,
  ]);

  const defaultProductionCastFormValues = React.useCallback(
    () => ({
      cast: productionUsedRoles?.rolesOptions || {},
      crew: productionUsedRoles?.professionsOptions || {},
    }),
    [productionUsedRoles],
  );

  // calendar dialog
  const [datesMenuAnchor, setDatesMenuAnchor] = React.useState(null);

  // time modal
  const [timeModalAnchor, setTimeModalAnchor] = React.useState(null);
  const [timeModalSubmitFunc, setTimeModalSubmitFunc] = React.useState(() => () => {});

  const [deleteTimeModalAnchor, setDeleteTimeModalAnchor] = React.useState(null);
  const [deleteTimeModalFunc, setDeleteTimeModalFunc] = React.useState(() => () => {});

  // cast modal
  const [castModalAnchor, setCastModalAnchor] = React.useState(null);
  const [castModalDateTime, setCastModalDateTimes] = React.useState({});
  const [castModalSubmitFunc, setCastModalSubmitFunc] = React.useState(() => () => {});

  // delete performance modal
  const [deletePerformanceModalAnchor, setDeletePerformanceModalAnchor] = React.useState(null);
  const [performanceToDeleteDate, setPerformanceToDeleteDate] = React.useState(null);

  // MODALS FUNCS
  // CALENDAR MODAL
  const handleClick = event => {
    setDatesMenuAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setDatesMenuAnchor(null);
  };

  // TIME MODAL
  const addTime = (e, addFunction, performanceFormValues) => {
    const timeArray = get(performanceFormValues, cardFields.time.name, []);
    setTimeModalAnchor(e.currentTarget);
    const defaultCasts = defaultProductionCastFormValues();
    setTimeModalSubmitFunc(() => time => {
      if (timeArray.length === 1 && timeArray[0].time === null) {
        addFunction(0, { ...timeArray[0], time });
      } else if (!find(timeArray, { time })) {
        addFunction({
          time,
          cast: performanceFormValues?.cast || defaultCasts?.cast || {},
          crew: performanceFormValues?.crew || defaultCasts?.crew || {},
          date: performanceFormValues?.date,
        });
      }
    });
  };

  const deleteTime = (e, data, deleteFunction, isPublished) => {
    if (isPublished) {
      setDeleteTimeModalAnchor(e.currentTarget);
      setDeleteTimeModalFunc(() => () => deleteFunction());
    } else {
      deleteFunction();
    }
  };

  const closeTimeModal = () => {
    setTimeModalAnchor(null);
    setTimeModalSubmitFunc(() => () => {});
  };

  const closeDeleteTimeModal = () => {
    setDeleteTimeModalAnchor(null);
    setDeleteTimeModalFunc(() => () => {});
  };

  // CAST MODAL
  const openCastModal = (e, times, performanceToUpdate) => {
    setCastModalAnchor(e.currentTarget);
    setCastModalDateTimes({ performanceToUpdate, times });
    const date = performanceToUpdate?.date;
    const ref = FormRef.current;
    setCastModalSubmitFunc(() => (isForWholePerformance, timeWithNewCasts) => {
      const values = { ...ref?.values };
      const updateFunc = ref?.resetForm;
      set(values, `${date}.time`, timeWithNewCasts);
      if (isForWholePerformance) {
        set(values, `${date}.cast`, timeWithNewCasts[0]?.cast || {});
        set(values, `${date}.crew`, timeWithNewCasts[0]?.crew || {});
      }
      updateFunc({ values });
    });
  };

  const closeCastModal = () => {
    setCastModalAnchor(null);
    setCastModalSubmitFunc(() => {});
    setCastModalDateTimes({});
  };

  // PERF MODAL
  const openDeletePerfModal = (e, perfDate) => {
    setDeletePerformanceModalAnchor(e.currentTarget);
    setPerformanceToDeleteDate(perfDate);
  };

  const closeDeletePerfModal = () => {
    setDeletePerformanceModalAnchor(null);
    setPerformanceToDeleteDate(null);
  };

  const removePrfDate = () => {
    const newDatesOfPerf = [...performancesChosenDates];
    const disabledDates = [...performancesDisabledDates];
    const values = { ...FormRef.current?.values };
    const updateFunc = FormRef.current?.resetForm;
    remove(newDatesOfPerf, d => dateSendFormat(d) === performanceToDeleteDate); // remove from displayed dates
    remove(disabledDates, d => dateSendFormat(d) === performanceToDeleteDate); // remove from disabled dates for calendar
    setPerformancesChosenDates(newDatesOfPerf);
    setPerformancesDisabledDates(disabledDates);
    delete values[performanceToDeleteDate]; // remove from values
    updateFunc({ values });
    closeDeletePerfModal();
  };

  const deleteTimeFromPerf = () => {
    deleteTimeModalFunc();
    closeDeleteTimeModal();
  };

  const searchVenue = debounce((fieldName, value, queryParams) => {
    dispatch(
      getList({
        endpoint: VENUES,
        sagaRoutine: getVenuesAction,
        params: {
          fieldName,
          queryParams: { query: value, show_invalid: true, visibility: VISIBILITY.ALL, ...queryParams },
        },
      }),
    );
  }, 300);

  const searchCities = debounce((fieldName, value, countryId) => {
    dispatch(
      getList({
        endpoint: COUNTRY_CITY_ENDPOINT(countryId),
        sagaRoutine: getPerformanceCitiesAction,
        fieldName,
        params: {
          queryParams: { query: value, show_invalid: true, visibility: VISIBILITY.ALL },
        },
      }),
    );
  }, 300);

  const searchStages = debounce((fieldName, value, venueId) => {
    dispatch(
      getList({
        endpoint: VENUES_STAGES_ENDPOINT(venueId),
        sagaRoutine: getPerformanceStagesAction,
        fieldName,
        params: {
          queryParams: { query: value, show_invalid: true, visibility: VISIBILITY.ALL },
        },
      }),
    );
  }, 300);

  const clearValues = valuesType => {
    switch (valuesType) {
      case cardFields.city.name:
        dispatch(clearPerformanceCitiesAction());
        break;
      case cardFields.venue.name:
        dispatch(cleanOptionsValuesAction('venues'));
        break;
      case cardFields.stage.name:
        dispatch(clearPerformanceStagesAction());
        break;
      default:
        break;
    }
  };

  const openDefaultTimeModal = () => {
    const ref = FormRef.current;
    setUpdateTimesFunc(() => formValues => {
      const values = { ...ref?.values };
      const updateFunc = ref?.resetForm;
      allLiveDatesOfProduction().forEach(date => {
        const dateKey = dateSendFormat(date);
        const performance = get(values, dateKey, {});
        const perfTimes = keyBy(get(performance, cardFields.time.name, []), 'time');
        if (get(perfTimes, 'null') && get(formValues, '[0]')) {
          const firstTime = get(formValues, '[0]');
          set(perfTimes, firstTime, mergeTimeWithCast(firstTime, get(performance, 'cast'), get(performance, 'crew'))); // to remove default time === time = undefined
          delete perfTimes.null;
        }
        formValues.forEach(time => {
          if (!get(perfTimes, time)) {
            set(
              perfTimes,
              time,
              mergeTimeWithCast(time, get(performance, 'cast'), get(performance, 'crew')), // set default cast from performance
            );
          }
        });
        set(values, `${dateKey}.${cardFields.time.name}`, Object.values(perfTimes));
      });
      updateFunc({ values });
      setDefaultTimeValues(formValues);
      setDefaultTimesModalState(false);
    });
    setDefaultTimesModalState(true);
  };

  const openDefaultVenuesModal = () => {
    const ref = FormRef.current;
    setUpdateVenuesFunc(() => formValues => {
      setDefaultVenueValues(formValues);
      const values = { ...ref?.values };
      const updateFunc = get(ref, 'resetForm', () => {});
      allLiveDatesOfProduction().forEach(date => {
        const dateKey = dateSendFormat(date);
        const performance = values[dateKey] || {};
        values[dateKey] = { ...performance, ...formValues };
      });
      updateFunc({ values });
      setDefaultVenueModalState(false);
      setUpdateVenuesFunc(() => () => {});
    });
    setDefaultVenueModalState(true);
  };

  const modifyValuesForUpdate = (date, perfValues = {}) => {
    const times = perfValues?.[cardFields.time.name] || [];
    // to send time object if time is not chosen
    if (times.length === 0) {
      times.push({
        time: null,
        casts: getPerformancesValuesForUpdate({ cast: perfValues?.cast || {}, crew: perfValues?.crew || {} }),
      });
    }
    const countryId = perfValues[cardFields.country.name]?.value;
    const cityId = perfValues[cardFields.city.name]?.value;
    const venueId = perfValues[cardFields.venue.name]?.value;
    const stageId = perfValues[cardFields.stage.name]?.value;

    const data = [];
    const performancesTypes = [];
    [PERFORMANCE_TYPES_ID.TOUR, PERFORMANCE_TYPES_ID.FESTIVAL, PERFORMANCE_TYPES_ID.CANCELLED].forEach(typeField => {
      if (perfValues[typeField]) performancesTypes.push({ id: Number(typeField) });
    });

    const prepareTimeValuesForUpdate = timeValues => ({
      ...(timeValues?.id ? { id: timeValues?.id } : {}),
      date,
      time: timeValues?.time,
      casts: getPerformancesValuesForUpdate({ cast: timeValues?.cast || {}, crew: timeValues?.crew || {} }),
      performanceTypes: performancesTypes,
      country: countryId ? { id: countryId } : null,
      city: cityId ? { id: cityId } : null,
      venue: venueId ? { id: venueId } : null,
      venueStage: stageId ? { id: stageId } : null,
      festival: get(perfValues, cardFields.isFestivalPart.name)
        ? { id: get(perfValues, `${cardFields.festival.name}.value`) }
        : null,
    });
    times.forEach(timeInfo => {
      const timeData = prepareTimeValuesForUpdate(timeInfo);
      data.push(timeData);
    });
    return data;
  };

  const submitFunc = values => {
    let datesToSave = [];
    Object.keys(values).forEach(dateValue => {
      const keyDate = dateSendFormat(dateValue);
      const dateInfo = values[keyDate];
      datesToSave = [...datesToSave, ...modifyValuesForUpdate(keyDate, dateInfo)];
    });
    dispatch(
      createEntity({
        endpoint: PRODUCTION_DATES(production?.id),
        sagaRoutine: updateProductionPerformancesAction,
        params: { dates: datesToSave },
        callback: doNextStep,
      }),
    );
  };

  const commonVenuesFieldValues = {
    searchVenue,
    searchCities,
    searchStages,
    loadingValue,
    venues: venuesOptions,
    cities: citiesOptions,
    stages: stagesOptions,
    clearValues,
  };

  return (
    <>
      <div>
        <Button variant="outlined" onClick={handleClick} startIcon={<TodayIcon />} className={classes.datesButton}>
          {t(`${TP}.FN_ADD_DATES_BUTTON`)}
        </Button>
        <Popover
          id={datesMenuAnchor ? 'dates' : null}
          anchorEl={datesMenuAnchor}
          handleClose={handleClose}
          className={classes.calendarPopup}
        >
          <EditProductionCalendar
            chosenDates={performancesChosenDates || []}
            disabledDates={performancesDisabledDates}
            submitDates={dates => {
              const newDates = dates.sort((a, b) => a.getTime() - b.getTime());
              setPerformancesChosenDates(newDates);
              updatePerformancesDates(newDates);
              handleClose();
            }}
            highlightDisabled
          />
        </Popover>
        {isArtistType && allLiveDatesOfProduction()?.length > 0 && (
          <SelectDatesCard
            selectedDates={playedDates}
            onDateClick={updateArtistDatesCast}
            roles={prodArtistCasts}
            profileId={editingProfile?.id}
            dates={allLiveDatesOfProduction()}
          />
        )}
        <Title size="big" style={{ fontWeight: 'bold' }} className={classes.performanceDetailsTitle}>
          {t(`${TP}.FN_ENTER_PERFORMANCE_DETAILS`)}
        </Title>
        <BulkBox
          title={
            <TitleWithInfo title={`${TP}.FN_STANDARD_PERFORMANCE_TIME`} tooltip={`${TP}.FN_PERFORMANCE_TIME_INFO`} />
          }
          buttonText={t(`${TP}.FN_ADD_TIME_BUTTON`)}
          applyFunc={openDefaultTimeModal}
        >
          <Title size="big">{defaultTimeValues.join(', ')}</Title>
        </BulkBox>
        <BulkBox
          title={t(`${TP}.FN_CHOOSE_VENUE`)}
          buttonText={t(`${TP}.ADD_VENUE`)}
          applyFunc={openDefaultVenuesModal}
        >
          <Title size="big">
            {compact([
              defaultVenueValues[cardFields?.country.name]?.label,
              defaultVenueValues[cardFields?.city.name]?.label,
              defaultVenueValues[cardFields?.venue.name]?.label,
              defaultVenueValues[cardFields?.stage.name]?.label,
            ]).join(', ')}
          </Title>
        </BulkBox>
        <Formik
          initialValues={{ ...existPerformancesValues }}
          onSubmit={(values, { setSubmitting }) => {
            submitFunc(values);
            setSubmitting(false);
          }}
          innerRef={FormRef}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Grid
                container
                spacing={2}
                style={{
                  width: '100%',
                }}
              >
                {Object.keys(values || [])
                  .sort()
                  .reverse()
                  .map(d => {
                    const performance = get(values, d, {});
                    const isPublishedPerformance = get(performance, 'isPublished', false);
                    return (
                      <Grid item key={d} sm={12} xs={12}>
                        {React.createElement(PerformanceEditCard, {
                          ...commonVenuesFieldValues,
                          addTime,
                          deleteTime,
                          deletePerformance: (e, perfDate) => openDeletePerfModal(e, perfDate),
                          performanceFormValues: performance,
                          parentFieldName: d,
                          festivalsOptions: getFestivalsOptions(),
                          setFieldValue,
                          openCastModal,
                          canBeDeleted: isPublishedPerformance ? canDeletePublishedPerformance() : true,
                          isFestival: isProductionType(production?.performanceTypes, PERFORMANCE_TYPES_ID.FESTIVAL),
                        })}
                      </Grid>
                    );
                  })}
              </Grid>
            </Form>
          )}
        </Formik>
      </div>
      <StepperButtonsBlock
        activeStepNumber={2}
        activeStep={{ backStepAction, nextStepAction, info: null }}
        nextStepText={`${TP}.m_NEXTSTEP`}
        isStepUpdating={datesLoading}
      />
      {Boolean(timeModalAnchor) && (
        <TimeAddModal
          t={t}
          isOpened={Boolean(timeModalAnchor)}
          onClose={closeTimeModal}
          submitFunc={time => {
            timeModalSubmitFunc(time);
            closeTimeModal();
          }}
        />
      )}
      {Boolean(castModalAnchor) && (
        <CastEditModal
          t={t}
          isOpened={Boolean(castModalAnchor)}
          onClose={closeCastModal}
          performance={castModalDateTime}
          production={production}
          professions={productionUsedRoles?.professions}
          roles={productionUsedRoles?.roles}
          rolesOptions={productionUsedRoles?.rolesOptions}
          professionsOptions={productionUsedRoles?.professionsOptions}
          submitFunc={(isForWholePerformance, selectedCasts) => {
            castModalSubmitFunc(isForWholePerformance, selectedCasts);
            closeCastModal();
          }}
        />
      )}
      {Boolean(deletePerformanceModalAnchor) && (
        <ConfirmDeleteModal
          closeModal={closeDeletePerfModal}
          onDelete={removePrfDate}
          title={t(`${TP}.FN_DELETE_PERFORMANCE_CONFIRMATION`)}
          text={t(`${TP}.FN_DELETE_PERFORMANCE_CONFIRMATION_INFO`)}
        />
      )}
      {Boolean(deleteTimeModalAnchor) && (
        <ConfirmDeleteModal
          closeModal={closeDeleteTimeModal}
          onDelete={deleteTimeFromPerf}
          title={t(`${TP}.FN_DELETE_PERFORMANCE_TIME_CONFIRMATION`)}
          text={t(`${TP}.FN_DELETE_PERFORMANCE_TIME_CONFIRMATION_INFO`)}
        />
      )}
      <StandardizedVenue
        {...commonVenuesFieldValues}
        setDefaultVenue={updateVenuesFunc}
        defaultValues={defaultVenueValues}
        onClose={() => setDefaultVenueModalState(false)}
        companyVenues={productionCompanyVenues?.data}
        isOpened={defaultVenueModalState}
      />
      {defaultTimesModalState && (
        <StandardizedTime
          timeArray={defaultTimeValues}
          applyStandardTimes={updateTimesFunc}
          isOpened={defaultTimesModalState}
          onClose={() => setDefaultTimesModalState(false)}
        />
      )}
    </>
  );
};

LiveEvents.propTypes = {
  className: PropTypes.string,
  editingProfile: PropTypes.object,
  production: PropTypes.object,
  sectionStep: PropTypes.number,
  stagesOptions: PropTypes.array,
  userConstants: PropTypes.array,
  datesLoading: PropTypes.bool,
  productionCompanyVenues: PropTypes.object,
  type: PropTypes.oneOf(['company', 'artist']),
};

const mapStateToProps = createStructuredSelector({
  productionUsedRoles: selectProductionUsedRolesAndProfessions(),
  casts: selectCasts(),
  existPerformancesValues: selectPerformancesFormValues(),
  loadingValue: selectLoadingFieldName(),
  venuesOptions: selectVenuesOptions(),
  citiesOptions: selectPerformanceCitiesOptions(),
  stagesOptions: selectPerformanceStagesOptions(),
  userConstants: selectUserConstants(),
  datesLoading: selectDatesLoading(),
  productionCompanyVenues: selectProductionCompanyVenues(),
  activeProfileId: selectActiveProfileId(),
  artistDates: selectArtistDates(),
});

const withConnect = connect(mapStateToProps, null);

export default compose(memo, withConnect)(LiveEvents);
